// Event RSVPs (2026-10-18; mockup: "FriSo Event RSVPs" canvas, https://claude.ai/artifact/2UtugsnAQZuKkahL7ArjeW).
// events/{id}.rsvp = { [email]: "yes" | "no" }; no key = hasn't answered. Only plain events take answers (not
// Busy / Away / schedules / unshared Google events), and the host (owner) counts as going. People write only
// their own key (firestore.rules isValidRsvp). A new answer notifies the host (notifications type "event_rsvp").
import { addDoc, collection, deleteField, doc, FieldPath, updateDoc } from "firebase/firestore";
import { db } from "./firebase";
import { getEventKind } from "./dateUtils";

export const takesRsvp = (ev) => !!ev && getEventKind(ev) === "event" && !ev.schedule && ev._source !== "google";

// Extra fields on an event_shared notification, so its card can offer Going / Can't go
export const rsvpNotifFields = (kind, startDate, startTime) =>
  kind === "event" ? { rsvp: true, startDate: startDate || null, startTime: startTime || null } : {};

// answer "yes" | "no" | null (take it back). eventId is the series doc, never an occurrence id.
export async function sendRsvp({ eventId, email, answer, prev, hostEmail, title, startDate }) {
  // FieldPath, not "rsvp.<email>": the dots in an email would be read as nested fields
  await updateDoc(doc(db, "events", eventId), new FieldPath("rsvp", email), answer || deleteField());
  if (!answer || answer === prev || !hostEmail?.includes("@") || hostEmail === email) return;
  await addDoc(collection(db, "notifications"), {
    type: "event_rsvp", actorEmail: email, recipientEmail: hostEmail, answer,
    eventTitle: title, eventId, startDate: startDate || null, status: "unread", createdAt: Date.now(),
  }).catch(() => {}); // the answer is saved either way
}

const LOOK = { host: ["yes", "✓", "Host"], yes: ["yes", "✓", "Going"], wait: ["wait", "?", "Hasn't answered"], no: ["no", "✕", "Can't go"] };

// A person's initials with an answer badge (✓ / ? / ✕)
export function RsvpFace({ initials, name, status, mine = false }) {
  const [tone, mark, label] = LOOK[status];
  return (
    <span className={`rsvp-face${mine ? " rsvp-face--me" : ""}`} role="img" aria-label={`${name} · ${label}`} title={`${name} · ${label}`}>
      {initials}
      <span className={`rsvp-badge rsvp-badge--${tone}`} aria-hidden="true">{mark}</span>
    </span>
  );
}

const statusOf = (ev, email) => (email === ev.ownerEmail ? "host" : ev.rsvp?.[email] || "wait");

// The pod rows of the event card: each pod chip with its members' answers under it, then the totals.
// Only pods the viewer is in are known here; someone in two pods shows once, under the first.
export function RsvpPods({ ev, pods, podColor, nameOf, initialsOf, me }) {
  const seen = new Set();
  const groups = (ev.sharedPods || []).filter((id) => pods[id]).map((id) => {
    const people = (pods[id].members || []).filter((e) => (ev.memberEmails || []).includes(e) && !seen.has(e));
    people.forEach((e) => seen.add(e));
    return { id, name: pods[id].name, people };
  });
  const all = [...seen].map((e) => statusOf(ev, e));
  const going = all.filter((s) => s === "host" || s === "yes").length;
  const waiting = all.filter((s) => s === "wait").length;
  const no = all.filter((s) => s === "no").length;
  return (
    <div className="rsvp-pods">
      {groups.map((g) => {
        const c = podColor(g.id);
        return (
          <div key={g.id} className="rsvp-pod">
            <span className="pg-podchip" style={{ background: c.bg, color: c.text }}>{g.name}</span>
            {g.people.length > 0 && (
              <div className="rsvp-faces">
                {g.people.map((e) => <RsvpFace key={e} initials={initialsOf(e)} name={nameOf(e)} status={statusOf(ev, e)} mine={e === me} />)}
              </div>
            )}
          </div>
        );
      })}
      {all.length > 1 && (
        <div className="rsvp-sum"><strong>{going} going</strong>{waiting > 0 && ` · ${waiting} haven't answered`}{no > 0 && ` · ${no} can't go`}</div>
      )}
    </div>
  );
}

// "Are you going?" + Going / Can't go. Pressing your current answer again takes it back.
export function RsvpAsk({ answer, onAnswer, busy }) {
  return (
    <div className="rsvp-ask">
      <span className="rsvp-ask__q">{answer === "yes" ? "You're going" : answer === "no" ? "You can't go" : "Are you going?"}</span>
      <div className="rsvp-ask__btns">
        <button type="button" className={`rsvp-btn${answer === "yes" ? " rsvp-btn--yes" : ""}`} aria-pressed={answer === "yes"} disabled={busy}
          onClick={() => onAnswer(answer === "yes" ? null : "yes")}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>Going
        </button>
        <button type="button" className={`rsvp-btn${answer === "no" ? " rsvp-btn--no" : ""}`} aria-pressed={answer === "no"} disabled={busy}
          onClick={() => onAnswer(answer === "no" ? null : "no")}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>Can't go
        </button>
      </div>
    </div>
  );
}
